import React, { useState, useCallback } from "react";
import { Config } from "@eagerdog/config";

import Button from "src/components/Button/Button";

import styles from "./EmbedModal.module.scss";

interface IProps {
  clubId: string;
  clubName: string;
}

const DEFAULT_EVENT_PRIMARY     = "#F25A2D";
const DEFAULT_EVENT_SECONDARY   = "#FFF6EB";
const DEFAULT_COURSE_PRIMARY    = "#4F6EF7";
const DEFAULT_COURSE_SECONDARY  = "#EEF2FF";

const ColorField: React.FC<{
  label: string;
  value: string;
  defaultValue: string;
  onChange: (v: string) => void;
}> = ({ label, value, defaultValue, onChange }) => (
  <div className={styles.colorField}>
    <label className={styles.colorLabel}>{label}</label>
    <div className={styles.colorInputWrap}>
      <input
        type="color"
        className={styles.colorSwatch}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <input
        type="text"
        className={styles.colorHex}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
        }}
        maxLength={7}
      />
      {value !== defaultValue && (
        <button className={styles.resetBtn} onClick={() => onChange(defaultValue)}>
          Reset
        </button>
      )}
    </div>
  </div>
);

const EmbedModal: React.FC<IProps> = ({ clubId, clubName }) => {
  const [copied, setCopied] = useState(false);
  const [eventPrimary,    setEventPrimary]    = useState(DEFAULT_EVENT_PRIMARY);
  const [eventSecondary,  setEventSecondary]  = useState(DEFAULT_EVENT_SECONDARY);
  const [coursePrimary,   setCoursePrimary]   = useState(DEFAULT_COURSE_PRIMARY);
  const [courseSecondary, setCourseSecondary] = useState(DEFAULT_COURSE_SECONDARY);

  const webUrl = Config.web.REACT_APP_WEB_URL || window.location.origin;

  const buildSrc = useCallback(() => {
    const params = new URLSearchParams();
    if (eventPrimary    !== DEFAULT_EVENT_PRIMARY)    params.set("primaryColor",        eventPrimary);
    if (eventSecondary  !== DEFAULT_EVENT_SECONDARY)  params.set("secondaryColor",      eventSecondary);
    if (coursePrimary   !== DEFAULT_COURSE_PRIMARY)   params.set("coursePrimaryColor",  coursePrimary);
    if (courseSecondary !== DEFAULT_COURSE_SECONDARY) params.set("courseSecondaryColor", courseSecondary);
    const qs = params.toString();
    return `${webUrl}/embed/club/${clubId}/calendar${qs ? `?${qs}` : ""}`;
  }, [webUrl, clubId, eventPrimary, eventSecondary, coursePrimary, courseSecondary]);

  const snippet = `<iframe\n  src="${buildSrc()}"\n  width="100%"\n  height="700"\n  style="border:none; border-radius:8px;"\n  title="${clubName} Calendar"\n  loading="lazy"\n  allowfullscreen>\n</iframe>`;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [snippet]);

  return (
    <div className={styles.EmbedModal}>
      <h3 className={styles.title}>Embed Calendar</h3>
      <p className={styles.description}>
        Copy the code below and paste it into your website to embed your club's public calendar.
      </p>

      <div className={styles.colorSection}>
        {/* Events row */}
        <p className={styles.colorGroupLabel}>Events</p>
        <div className={styles.colorRow}>
          <ColorField
            label="Primary"
            value={eventPrimary}
            defaultValue={DEFAULT_EVENT_PRIMARY}
            onChange={setEventPrimary}
          />
          <ColorField
            label="Secondary"
            value={eventSecondary}
            defaultValue={DEFAULT_EVENT_SECONDARY}
            onChange={setEventSecondary}
          />
        </div>

        {/* Courses row */}
        <p className={styles.colorGroupLabel}>Courses</p>
        <div className={styles.colorRow}>
          <ColorField
            label="Primary"
            value={coursePrimary}
            defaultValue={DEFAULT_COURSE_PRIMARY}
            onChange={setCoursePrimary}
          />
          <ColorField
            label="Secondary"
            value={courseSecondary}
            defaultValue={DEFAULT_COURSE_SECONDARY}
            onChange={setCourseSecondary}
          />
        </div>

        {/* Preview */}
        <div className={styles.preview}>
          <span className={styles.previewChip}
            style={{ backgroundColor: eventSecondary, color: eventPrimary, borderColor: eventPrimary }}>
            Event
          </span>
          <span className={styles.previewChip}
            style={{ backgroundColor: courseSecondary, color: coursePrimary, borderColor: coursePrimary }}>
            Course
          </span>
          <span className={styles.previewTile}
            style={{ backgroundColor: eventSecondary, borderLeftColor: eventPrimary }}>
            <span style={{ background: eventPrimary }} className={styles.previewDot} />
            Agility Trial
          </span>
          <span className={styles.previewTile}
            style={{ backgroundColor: courseSecondary, borderLeftColor: coursePrimary }}>
            <span style={{ background: coursePrimary }} className={styles.previewDot} />
            Nosework Course
          </span>
        </div>
      </div>

      <div className={styles.snippetWrap}>
        <textarea
          className={styles.snippet}
          readOnly
          value={snippet}
          rows={8}
          onClick={(e) => (e.target as HTMLTextAreaElement).select()}
        />
      </div>
      <div className={styles.actions}>
        <Button onClick={handleCopy}>
          {copied ? "Copied!" : "Copy Code"}
        </Button>
      </div>
    </div>
  );
};

export default EmbedModal;
